import React, { useState, useEffect } from 'react';
import StatsGrid from './StatsGrid';
import LevelProgress from './LevelProgress';
import ActivityFeed from './ActivityFeed';
import ExamsFeed from './ExamsFeed';
import Skills from './Skills';
import ActivityChartCard from './ActivityChartCard';
import SkillsRadarChart from './SkillsRadarChart';
import StudyTimeDoughnutChart from './StudyTimeDoughnutChart';
import './DashboardView.css';

function formatStudyTime(totalSeconds) {
    if (!totalSeconds) return "0m";
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

// Recibe summaryData directamente desde DashboardPage (fuente única de verdad)
function DashboardView({ summaryData }) {
    const [levelsData,       setLevelsData]       = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [recentExams,      setRecentExams]      = useState([]);
    const [monthlyActivity,  setMonthlyActivity]  = useState([]);
    const [isLoading,        setIsLoading]        = useState(true);
    const [fetchError,       setFetchError]       = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const fetchData = async () => {
            try {
                const responses = await Promise.allSettled([
                    fetch("/api/levels", { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch("/api/progress/recent-activities", { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch("/api/progress/monthly-activity", { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch("/api/v3/exams/recientes", { headers: { 'Authorization': `Bearer ${token}` } }),
                ]);

                if (responses[0].status === 'fulfilled' && responses[0].value.ok) 
                    setLevelsData(await responses[0].value.json());
                if (responses[1].status === 'fulfilled' && responses[1].value.ok) 
                    setRecentActivities(await responses[1].value.json());
                if (responses[2].status === 'fulfilled' && responses[2].value.ok) {
                    const monthlyData = await responses[2].value.json();
                    setMonthlyActivity(monthlyData.activity);
                }
                if (responses[3].status === 'fulfilled' && responses[3].value.ok) {
                    const examsData = await responses[3].value.json();
                    setRecentExams(examsData.items || []);
                }
            } catch (error) {
                console.error("Error al obtener datos del dashboard:", error);
                setFetchError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const totalStudyTime = formatStudyTime(summaryData?.total_study_seconds);

    if (isLoading) {
        return (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                ⏳ Cargando Dashboard…
            </p>
        );
    }

    return (
        <>
            {/* Banner de error de conexión */}
            {fetchError && (
                <div style={{
                    padding: '10px 16px', borderRadius: 10, marginBottom: 16,
                    background: 'rgba(239,68,68,.08)',
                    border: '1px solid rgba(239,68,68,.25)',
                    color: '#dc2626', fontSize: '.85rem', fontWeight: 600,
                }}>
                    ⚠️ Algunos datos no pudieron cargarse. Verifica tu conexión y recarga la página.
                </div>
            )}
            <StatsGrid summaryData={summaryData} totalStudyTime={totalStudyTime} />
            
            <div className="dp-progress-section">
                <LevelProgress summaryData={summaryData} levelsData={levelsData} />
                <Skills skillScores={summaryData?.skill_scores} />
            </div>
            
            <ActivityChartCard activityData={monthlyActivity} />
            
            <div className="dp-advanced-charts-grid">
                <div className="dp-activity-card dp-neumorphic">
                    <h2 className="dp-section-title">Radar de Habilidades</h2>
                    <div style={{ height: '300px', position: 'relative' }}>
                        <SkillsRadarChart skillScores={summaryData?.skill_scores} />
                    </div>
                </div>
                <div className="dp-activity-card dp-neumorphic">
                    <h2 className="dp-section-title">Tiempo por Habilidad</h2>
                    <div style={{ height: '300px', position: 'relative' }}>
                        <StudyTimeDoughnutChart studyTimeBreakdown={summaryData?.study_time_breakdown} />
                    </div>
                </div>
            </div>
            
            <div className="dp-activities-section" style={{marginTop: '1.5rem'}}>
                <ActivityFeed recentActivities={recentActivities} />
                <ExamsFeed exams={recentExams} />
            </div>
        </>
    );
}

export default DashboardView;